import axios, { AxiosError, AxiosResponse, Method } from 'axios';
import { HttpClient } from './httpClient';
import { ApiError, isApiErrorParams } from '../error/apiError';
import { ThrownError } from '../error/errorHandler';

class BackendHttpClient implements HttpClient {
  private readonly baseApiUrl = process.env.REACT_APP_BACKEND_REST_API_URL;

  public get<T>(url: string): Promise<T> {
    return this.sendRequest('GET', url);
  }

  public post<T>(url: string, body: {}): Promise<T> {
    return this.sendRequest('POST', url, body);
  }

  public put<T>(url: string, body: {}): Promise<T> {
    return this.sendRequest('PUT', url, body);
  }

  public delete<T>(url: string): Promise<T> {
    return this.sendRequest('DELETE', url);
  }

  private async sendRequest<T>(method: Method, url: string, body?: {}): Promise<T> {
    const requestUrl = url.startsWith('/')
      ? `${this.baseApiUrl}${url}`
      : `${this.baseApiUrl}/${url}`;

    try {
      const response: AxiosResponse<T> = await axios.request({
        method,
        url: requestUrl,
        data: body,
        responseType: 'json',
        withCredentials: true,
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw this.handleAxiosError(error);
      } else {
        throw new Error(`Unknown error while connecting to backend API. Details: ${JSON.stringify(error)}`);
      }
    }
  }

  private handleAxiosError(axiosError: AxiosError): ThrownError {
    const responseData = axiosError?.response?.data;

    if (isApiErrorParams(responseData)) {
      const apiError: ApiError = {
        error: responseData.error,
        message: responseData.message,
        path: responseData.path,
        status: responseData.status,
        timestamp: responseData.timestamp,
        details: responseData.details,
      };

      throw apiError;
    }

    if (axiosError.response) {
      return new Error(`Error while sending request to backend API. Response: ${JSON.stringify(axiosError.request)}`);
    }

    if (axiosError.request) {
      return new Error(`Error while processing request to backend API. Request: ${JSON.stringify(axiosError.request)}`);
    }

    return new Error(`Error while setting up request to backend API. Details: ${axiosError.message}`);
  }
}

export default new BackendHttpClient();
